import { r as createLogger } from "../logger-CdXeOlrX.js";
import { recordedGpsEventPayloads } from "../utils/gps-event-actions.js";
import { n as DEFAULT_ARCHIVE_LIMITS } from "../archive-limits-CMo2zcn-.js";
import { i as readZipEntryText, t as DecompressionBudget } from "../capped-zip-entries-CIyZmVN5.js";
import { unpackDepthAction } from "./depth-sample-codec.js";
import { GPS_POINT_SOURCE_DEVICE, gpsPointSourceOf } from "gps-plus-slam-js";
import { BlobReader, TextWriter, Uint8ArrayReader, ZipReader } from "@zip.js/zip.js";
//#region ../src/storage/zip-reader.ts
/**
* Zip Reader — utilities for reading recording session zip files.
*
* Provides production-ready functions to enumerate zip entries, extract
* recorded Redux actions, and load session metadata from exported recording
* zip files. Works with both Node.js (Uint8Array from fs) and browser
* (Uint8Array from fetch/FileReader) environments.
*
* The recording zip format:
*   actions/000001.json  — Redux action JSON files (1-based, zero-padded)
*   actions/000002.json
*   ...
*   images/frame-000001.jpg  — captured image frames (optional; legacy: frames/)
*   session.json             — session metadata (optional, see F1 bug)
*/
const log = createLogger("ZipReader");
/**
* Wrap a {@link ZipSource} in a fresh zip.js ZipReader.
*
* Exported deliberately: consumers outside this workspace resolve this
* module from the published dist at runtime and probe
* `typeof toZipReader === 'function'` as the capability marker for lazy
* Reader support before passing anything other than Uint8Array (older
* published versions accept only Uint8Array). Do not rename or remove
* without treating it as a breaking API change.
*/
function toZipReader(source) {
	return new ZipReader(source instanceof Uint8Array ? new Uint8ArrayReader(source) : source);
}
/**
* Maximum allowed uncompressed size (in bytes) for a single action or
* metadata JSON file inside a recording zip. Entries exceeding this limit
* are rejected before decompression to prevent out-of-memory conditions
* from malicious or corrupted zip files.
*
* 1 MB — generous for Redux action JSON; typical actions are a few KB.
*/
const MAX_ACTION_FILE_SIZE = 1048576;
/**
* Read all entries from a zip file.
*
* @param data - The zip content as a Uint8Array, or a zip.js Reader for lazy access
* @returns Array of zip entries (directories and files)
*/
async function readZipEntries(data) {
	const reader = toZipReader(data);
	try {
		return await reader.getEntries();
	} finally {
		await reader.close();
	}
}
/**
* An `actions/` path SEGMENT: at the start, or after a slash. A wrapping
* folder (`mytour/actions/000001.json`, what re-zipping a folder produces)
* reads; a sibling whose name only ends in it (`myactions/`,
* `old-actions/`) is never replayed into the action stream.
*/
const ACTIONS_SEGMENT = /(?:^|\/)actions\//;
/** An action JSON entry name: under an `actions/` segment, ending `.json`. */
function isActionEntryName(filename) {
	return ACTIONS_SEGMENT.test(filename) && filename.endsWith(".json");
}
/** `session.json` as a whole path segment, at the root or in a wrapping
*  folder (`./session.json` included); a look-alike such as
*  `backup-session.json` is not the session. */
const SESSION_JSON_ENTRY = /(?:^|\/)session\.json$/;
/**
* Is `filename` a recording's `session.json`? The ONE rule for every
* reader and writer of it (`zip-coverage-embed` rewrites the entry this
* picks), so they never disagree about which file is the session.
*/
function isSessionJsonEntryName(filename) {
	return SESSION_JSON_ENTRY.test(filename);
}
/**
* Extract the numeric index from an action filename.
* Filenames are zero-padded like "000001.json" or "actions/000001.json".
*
* @param filename - The entry filename from the zip
* @returns The numeric index, or NaN if the filename doesn't match the pattern
*/
function extractActionIndex(filename) {
	const match = /(\d+)\.json$/.exec(filename);
	return match ? parseInt(match[1], 10) : NaN;
}
/**
* The caller's decompression budget, or one for the whole archive read
* through `data`.
* Called after the entries were listed, so a lazy Reader knows its size;
* one that still reports none gets the absolute ceiling rather than the
* small-archive floor, which would refuse a real long recording.
*/
function budgetFor(data, given) {
	if (given !== void 0) return given;
	const size = data instanceof Uint8Array ? data.length : data.size;
	return Number.isSafeInteger(size) && size > 0 ? DecompressionBudget.forArchive(size) : new DecompressionBudget({
		maxEntryBytes: DEFAULT_ARCHIVE_LIMITS.maxEntryBytes,
		maxTotalBytes: DEFAULT_ARCHIVE_LIMITS.maxTotalBytes
	});
}
/**
* Load all recorded Redux actions from a zip file.
*
* Filters for JSON files in the actions/ directory, parses them, and returns
* them sorted by filename. That is chronological order for replay because
* `formatActionFilename` zero-pads the index to six digits, so lexicographic
* and numeric order agree; the numeric index is extracted only to warn about
* a name that does not fit the scheme.
*
* @param data - The zip content as a Uint8Array, or a zip.js Reader for lazy access
* @param maxFileSize - Maximum allowed uncompressed size per entry (defaults to MAX_ACTION_FILE_SIZE),
*   checked against the declared size up front AND against the bytes actually inflated
* @param budget - The archive's decompression allowance (`capped-zip-entries`); a
*   caller reading several parts of one archive passes one budget to all of them.
*   Defaults to one sized from the archive (`DecompressionBudget.forArchive`).
* @returns Array of action entries sorted by filename
* @throws ArchiveLimitError when the inflated bytes pass a cap
*/
async function loadActionsFromZip(data, maxFileSize = MAX_ACTION_FILE_SIZE, budget) {
	const entries = await readZipEntries(data);
	const allowance = budgetFor(data, budget);
	return loadActionsFromEntries(entries, (entry, maxBytes) => readZipEntryText(entry, allowance, maxBytes), maxFileSize);
}
/**
* The action one entry's text holds, or null - with a warning naming the
* file - when it holds none: malformed JSON, no `type` string, or a
* damaged packed depth grid. A packed depth sample (scan pass S2,
* `depth-sample-codec.ts`) gets its points back here, so every reader sees
* the action as it was dispatched.
*/
function parseActionText(text, filename) {
	let parsed;
	try {
		parsed = JSON.parse(text);
	} catch {
		log.warn(`Skipping malformed JSON in "${filename}": parse failed`);
		return null;
	}
	if (typeof parsed !== "object" || parsed === null || typeof parsed.type !== "string") {
		log.warn(`Skipping "${filename}": parsed JSON is not a valid action (missing "type" string)`);
		return null;
	}
	const unpacked = unpackDepthAction(parsed);
	if (!unpacked.ok) {
		log.warn(`Skipping "${filename}": ${unpacked.reason}`);
		return null;
	}
	return unpacked.action;
}
/**
* The parse of {@link loadActionsFromZip} over an entry list the caller
* ALREADY HOLDS, each action entry read through `readText`. For a caller
* that has checked its list and must not let a second read of the
* directory replace it: the Tour Viewer reads a signed tour's recording
* from the list tier 1 checked, each entry hashed by its own reader (tour
* kit plan K1, milestone review R1) - re-listing a live range source could
* return another directory than the one that was checked.
*
* @param entries - The archive's entries (directories are skipped)
* @param read - Reads one action entry's text, given the per-file cap;
*   its rejection is the parse's
* @param maxFileSize - As for {@link loadActionsFromZip}
*/
async function loadActionsFromEntries(entries, read, maxFileSize = MAX_ACTION_FILE_SIZE) {
	const actionEntries = entries.filter((e) => !e.directory && isActionEntryName(e.filename)).sort((a, b) => a.filename.localeCompare(b.filename));
	const results = [];
	for (const entry of actionEntries) if (!entry.directory) {
		if (entry.uncompressedSize > maxFileSize) throw new Error(`Action file "${entry.filename}" (${entry.uncompressedSize} bytes) exceeds maximum allowed size of ${maxFileSize} bytes`);
		const index = extractActionIndex(entry.filename);
		if (Number.isNaN(index)) log.warn(`Unexpected non-numeric action filename: "${entry.filename}". Expected pattern like "actions/000001.json". The file will still be processed.`);
		const action = parseActionText(await read(entry, maxFileSize), entry.filename);
		if (action === null) continue;
		results.push({
			index,
			filename: entry.filename,
			action
		});
	}
	return results;
}
/**
* Extract session metadata from an already-constructed ZipReader.
*
* Finds the `session.json` entry, validates its size, extracts and parses it.
* The reader is always closed in `finally`, so callers must not reuse it.
*
* @param reader - An open ZipReader (Uint8ArrayReader or BlobReader based)
* @param maxFileSize - Maximum allowed uncompressed size for session.json
* @returns Parsed metadata object, or null if session.json is absent
*/
async function extractSessionMetadataFromReader(reader, maxFileSize) {
	try {
		const sessionEntry = (await reader.getEntries()).find((e) => !e.directory && isSessionJsonEntryName(e.filename));
		if (!sessionEntry) return null;
		if (sessionEntry.uncompressedSize > maxFileSize) throw new Error(`Session file "${sessionEntry.filename}" (${sessionEntry.uncompressedSize} bytes) exceeds maximum allowed size of ${maxFileSize} bytes`);
		const text = await readZipEntryText(sessionEntry, new DecompressionBudget({
			maxEntryBytes: maxFileSize,
			maxTotalBytes: maxFileSize
		}));
		return JSON.parse(text);
	} finally {
		await reader.close();
	}
}
/**
* Load session metadata from session.json in the zip file.
*
* Returns null if session.json is absent (graceful degradation for
* recordings created before the metadata writing bug was fixed).
*
* @param data - The zip content as a Uint8Array, or a zip.js Reader for lazy access
* @param maxFileSize - Maximum allowed uncompressed size per entry (defaults to MAX_ACTION_FILE_SIZE)
* @returns Parsed session metadata, or null if not found
*/
async function loadSessionMetadata(data, maxFileSize = MAX_ACTION_FILE_SIZE) {
	return extractSessionMetadataFromReader(toZipReader(data), maxFileSize);
}
/**
* Load session metadata from session.json in a zip file provided as a Blob.
*
* Unlike `loadSessionMetadata(Uint8Array)`, this variant uses zip.js's
* BlobReader which reads only the central directory and the target entry
* from the Blob — not the entire file contents. This is critical for
* memory-efficient scanning of many large recording zips (e.g., during
* `discoverScenariosFromZipMetadata`).
*
* Since `File` extends `Blob`, this works directly with file handles:
*   const file = await handle.getFile();
*   const meta = await loadSessionMetadataFromBlob(file);
*
* @param blob - A Blob or File containing the zip data
* @param maxFileSize - Maximum allowed uncompressed size per entry (defaults to MAX_ACTION_FILE_SIZE)
* @returns Parsed session metadata, or null if session.json is not found
*/
async function loadSessionMetadataFromBlob(blob, maxFileSize = MAX_ACTION_FILE_SIZE) {
	return extractSessionMetadataFromReader(new ZipReader(new BlobReader(blob)), maxFileSize);
}
function isDeviceFixWithCoords(gps) {
	return !!gps && typeof gps.latitude === "number" && typeof gps.longitude === "number" && gpsPointSourceOf(gps) === GPS_POINT_SOURCE_DEVICE;
}
/** One recorded GPS event payload's track point, or null when it has no
*  numeric coordinates or is not a device fix. Reads both the old
*  (`gpsPoint`) and the new (`rawGpsPoint`) payload format. A synthetic QR
*  vote sits up to 30 m along its code's face, where nobody walked, so it is
*  no track point (and no coverage); neither is a reading whose source stamp
*  is unknown - the core's `gpsPointSourceOf` never rounds that to device. */
function gpsPathCoordOf(event) {
	if (typeof event !== "object" || event === null) return null;
	const payload = event;
	const gps = payload.rawGpsPoint ?? payload.gpsPoint;
	if (!isDeviceFixWithCoords(gps)) return null;
	const accuracy = typeof gps.latLongAccuracy === "number" && gps.latLongAccuracy > 0 ? gps.latLongAccuracy : void 0;
	return {
		lat: gps.latitude,
		lng: gps.longitude,
		...accuracy !== void 0 ? { accuracy } : {}
	};
}
/**
* Extract GPS coordinates from a recording zip provided as a Blob.
*
* Uses BlobReader for memory-efficient reading. Reads all action JSON files,
* identifies the GPS actions (`gpsData/recordGpsEvent`, and every event of a
* `gpsData/recordGpsEventBatch`), and returns only the lightweight
* `{ lat, lng }` pairs — all other action data is discarded immediately.
*
* Returns coordinates in chronological order (sorted by action filename).
* Returns an empty array if the zip is invalid or contains no GPS actions.
*
* @param blob - A Blob or File containing the zip data
* @param maxFileSize - Maximum allowed uncompressed size per action entry
* @returns Array of GPS coordinates in chronological order
*/
async function loadGpsPathFromBlob(blob, maxFileSize = MAX_ACTION_FILE_SIZE) {
	const reader = new ZipReader(new BlobReader(blob));
	try {
		const actionEntries = (await reader.getEntries()).filter((e) => !e.directory && isActionEntryName(e.filename)).sort((a, b) => a.filename.localeCompare(b.filename));
		const coords = [];
		for (const entry of actionEntries) {
			if (entry.uncompressedSize > maxFileSize) continue;
			const text = await entry.getData(new TextWriter());
			let action;
			try {
				action = JSON.parse(text);
			} catch {
				continue;
			}
			for (const event of recordedGpsEventPayloads(action)) {
				const coord = gpsPathCoordOf(event);
				if (coord !== null) coords.push(coord);
			}
		}
		return coords;
	} catch {
		return [];
	} finally {
		try {
			await reader.close();
		} catch {}
	}
}
/**
* Enumerate every file under a single top-level subdirectory of a recording
* zip. Mirrors the writer-side {@link ZipExportContributor} seam so consumers
* (typically the recorder) can read back what they wrote without
* re-implementing zip enumeration.
*
* Skips directory entries and files outside `subdir`. Sorts entries by
* `relativePath` for deterministic iteration. Returns an empty array when
* the subdir is absent (graceful degradation for older zips).
*
* @param data - The zip content as a Uint8Array, or a zip.js Reader for lazy access
* @param subdir - Top-level subdir to scan (no leading or trailing `/`)
*/
async function loadEntriesFromSubdir(data, subdir) {
	if (!subdir || subdir.includes("/") || subdir.startsWith(".")) throw new Error(`subdir must be a non-empty single path segment, got: ${JSON.stringify(subdir)}`);
	const prefix = `${subdir}/`;
	return (await readZipEntries(data)).filter((e) => !e.directory && e.filename.startsWith(prefix) && e.filename !== prefix).sort((a, b) => a.filename.localeCompare(b.filename)).map((entry) => ({
		relativePath: entry.filename.slice(prefix.length),
		fullPath: entry.filename,
		uncompressedSize: entry.uncompressedSize,
		getText: () => entry.getData(new TextWriter())
	}));
}
//#endregion
export { MAX_ACTION_FILE_SIZE, isSessionJsonEntryName, loadActionsFromEntries, loadActionsFromZip, loadEntriesFromSubdir, loadGpsPathFromBlob, loadSessionMetadata, loadSessionMetadataFromBlob, readZipEntries, toZipReader };
